"use client";

import { useRef, useState } from "react";
import { Button } from "@tillix/ui-atoms/button";
import { Fieldset } from "@tillix/ui-atoms/fieldset";

const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface DocsSectionProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
}

export default function DocsSection({
  files,
  onFilesChange,
}: DocsSectionProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    onFilesChange([...files, ...Array.from(newFiles)]);
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <Fieldset className="max-w-full">
      <Fieldset.Legend>Documents</Fieldset.Legend>

      {/** biome-ignore lint/a11y/noStaticElementInteractions: <NOTE: need to disable this rule> */}
      <div
        className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors ${
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25"
        }`}
        onDragLeave={() => setIsDragOver(false)}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <svg
          className="size-10 text-muted-foreground"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Drag and drop file</title>
          <path
            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="text-muted-foreground text-sm">
          Drag and drop files here
        </p>
        <Button
          onClick={() => fileInputRef.current?.click()}
          size="sm"
          type="button"
          variant="outline"
        >
          Browse Files
        </Button>
        <input
          accept={ACCEPTED_TYPES}
          className="hidden"
          multiple={true}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
          ref={fileInputRef}
          type="file"
        />
      </div>

      {files.length > 0 && (
        <ul className="flex flex-col gap-2">
          {files.map((file, index) => (
            <li
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
              key={`${file.name}-${index}`}
            >
              <div className="flex flex-col">
                <span className="font-medium">{file.name}</span>
                <span className="text-muted-foreground text-xs">
                  {formatFileSize(file.size)}
                </span>
              </div>
              <button
                className="text-muted-foreground transition-colors hover:text-destructive"
                onClick={() => removeFile(index)}
                type="button"
              >
                <svg
                  className="size-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Remove file</title>
                  <path
                    d="M6 18 18 6M6 6l12 12"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Fieldset>
  );
}
